// PR-WATER-SUPPLIERS MAY 11 — supplier list + topup drawer.
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Loader2, AlertTriangle, Droplet } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/Sheet';
import { PageError } from '@/components/PageError';
import { apiClient, extractErrorMessage, getStoredToken } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { PasteableUpload } from '@/components/common/PasteableUpload';

const API_BASE = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';

interface Supplier {
  id: string;
  code: string;
  name_en: string;
  name_ar: string | null;
  status: string;
  primary_contact_name: string | null;
  primary_contact_phone: string | null;
  balance_sar: string | null;
  total_topped_up_sar: string | null;
  total_consumed_sar: string | null;
  low_balance_threshold_sar: string | null;
  last_topup_at: string | null;
  last_fill_at: string | null;
  low_balance: boolean | null;
}

const fmt = (n: number | string | null | undefined) => 'SAR ' + Number(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleString('en-GB') : '—');

export default function WaterSuppliersPage() {
  const { user } = useAuth();
  const canTopup = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'accountant';

  const [items, setItems] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [topupFor, setTopupFor] = useState<Supplier | null>(null);
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [reference, setReference] = useState('');
  const [topupNotes, setTopupNotes] = useState('');
  // PR-UPLOAD-TOPUP MAY 11 — receipt URL captured by PasteableUpload, then
  // sent to /topup as receiptPhotoUrl. Cleared every time the drawer opens.
  const [receiptUrl, setReceiptUrl] = useState<string | null>(null);
  const [receiptType, setReceiptType] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  async function uploadReceipt(file: File): Promise<void> {
    if (!topupFor) throw new Error('No supplier selected');
    const fd = new FormData();
    fd.append('file', file);
    const jwt = getStoredToken();
    const res = await fetch(`${API_BASE}/api/v1/water-suppliers/${topupFor.id}/upload-receipt`, {
      method: 'POST',
      headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
      body: fd,
    });
    if (!res.ok) {
      let detail = `${res.status} ${res.statusText}`;
      try { const j = await res.json(); if (j?.message || j?.error) detail = j.message ?? j.error; } catch { /* */ }
      throw new Error('Upload failed: ' + detail);
    }
    const body = await res.json();
    setReceiptUrl(body.url);
    setReceiptType(body.type);
  }

  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await apiClient.get<{ items: Supplier[] }>('/api/v1/water-suppliers');
      setItems(r.data.items ?? []);
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  async function performTopup() {
    if (!topupFor) return;
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) { setSaveError('Enter a positive amount'); return; }
    setSaving(true); setSaveError(null);
    try {
      await apiClient.post('/api/v1/water-suppliers/' + topupFor.id + '/topup', {
        amountSar: amt,
        paymentMethod,
        paymentReference: reference || null,
        receiptPhotoUrl: receiptUrl || null,
        notes: topupNotes || null,
      });
      setTopupFor(null); setAmount(''); setReference(''); setTopupNotes('');
      setReceiptUrl(null); setReceiptType(null);
      await refresh();
    } catch (e) { setSaveError(extractErrorMessage(e)); }
    finally { setSaving(false); }
  }

  const totalBalance = items.reduce((sum, s) => sum + Number(s.balance_sar ?? 0), 0);
  const anyLow = items.some((s) => s.low_balance);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Droplet className="h-4 w-4" /> Water suppliers</div>
          <Link to="/dashboard/water-stations"><Button variant="outline" size="sm">Stations</Button></Link>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="rounded-lg border border-border p-3">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Total wallet balance</div>
            <div className="text-2xl font-light mt-1">{fmt(totalBalance)}</div>
          </div>
          <div className="rounded-lg border border-border p-3">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Suppliers</div>
            <div className="text-2xl font-light mt-1">{items.length}</div>
          </div>
          <div className={'rounded-lg border p-3 ' + (anyLow ? 'border-amber-500/40 bg-amber-500/5' : 'border-border')}>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Low balance alerts</div>
            <div className={'text-2xl font-light mt-1 ' + (anyLow ? 'text-amber-700 dark:text-amber-400' : '')}>{items.filter((s) => s.low_balance).length}</div>
          </div>
        </div>

        {error && <PageError error={error} />}

        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…</div>
        ) : (
          <div className="rounded-lg border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground border-b border-border"><tr>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium">Code</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium">Supplier</th>
                <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium">Balance</th>
                <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium">Topped up</th>
                <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium">Consumed</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium">Last top-up</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium">Last fill</th>
                <th className="text-end px-4 py-3 text-xs uppercase tracking-wider font-medium w-24">{/* actions */}</th>
              </tr></thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.id} className="border-b border-border last:border-b-0 hover:bg-muted/40">
                    <td className="px-4 py-3 font-mono text-xs">{s.code}</td>
                    <td className="px-4 py-3">
                      <div>{s.name_en}</div>
                      {s.name_ar && <div className="text-xs text-muted-foreground" dir="rtl">{s.name_ar}</div>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      <span className={s.low_balance ? 'text-amber-700 dark:text-amber-400' : ''}>{fmt(s.balance_sar)}</span>
                      {s.low_balance && (
                        <div className="text-[10px] text-amber-700 dark:text-amber-400 inline-flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" /> low
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-muted-foreground">{fmt(s.total_topped_up_sar)}</td>
                    <td className="px-4 py-3 text-right font-mono text-muted-foreground">{fmt(s.total_consumed_sar)}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{fmtDate(s.last_topup_at)}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{fmtDate(s.last_fill_at)}</td>
                    <td className="px-4 py-3 text-end">
                      {canTopup && (
                        <Button size="sm" variant="gold" onClick={() => { setTopupFor(s); setAmount(''); setReference(''); setTopupNotes(''); setSaveError(null); }} className="gap-1">
                          <Plus className="h-3 w-3" /> Top up
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-8 text-sm text-muted-foreground italic">No suppliers yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Top-up drawer */}
      <Sheet open={!!topupFor} onOpenChange={(o) => {
        if (!o) {
          setTopupFor(null);
          // PR-UPLOAD-TOPUP MAY 11 — dropping the drawer mid-flow must
          // clear the staged receipt so the next supplier's drawer doesn't
          // inherit it. (The bytes are already on disk under the previous
          // supplier's id; that's fine, the URL just goes unreferenced.)
          setReceiptUrl(null); setReceiptType(null); setSaveError(null);
        }
      }}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Top up · {topupFor?.name_en}</SheetTitle>
            <SheetDescription>Add prepaid funds to the {topupFor?.code} wallet. Current balance: {fmt(topupFor?.balance_sar)}</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-6 space-y-3 text-sm">
            <div className="space-y-1">
              <Label>Amount (SAR) *</Label>
              <Input type="number" step="0.01" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} className="font-mono" placeholder="50000.00" />
            </div>
            <div className="space-y-1">
              <Label>Payment method *</Label>
              <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
                <option value="bank_transfer">Bank transfer</option>
                <option value="cash">Cash</option>
                <option value="cheque">Cheque</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label>Reference</Label>
              <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="BANK-TRF-001" />
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <textarea value={topupNotes} onChange={(e) => setTopupNotes(e.target.value)} rows={3} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
            <div className="space-y-1">
              <Label>Receipt (optional)</Label>
              <PasteableUpload
                helperText="Bank slip, screenshot, or PDF · click, drop, or paste (Cmd+V)"
                accept="image/*,application/pdf"
                maxSizeMB={10}
                currentFileUrl={receiptUrl}
                currentFileType={receiptType}
                onReplace={() => { setReceiptUrl(null); setReceiptType(null); }}
                onUpload={uploadReceipt}
              />
            </div>
            {saveError && <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm">{saveError}</div>}
          </div>
          <div className="shrink-0 border-t border-border bg-muted/30 p-6 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setTopupFor(null)} disabled={saving}>Cancel</Button>
            <Button onClick={performTopup} disabled={saving || !amount} className="gap-1">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Confirm top-up
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
